import { InputAutosuggest, Spinner } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { SearchAddressResponse, useGetAddressQuery } from '../../services/redux/features/api/searchAddress.api-slice';
import { useValidateAddressQuery } from '../../services/redux/features/api/validateAddress.api-slice';
import { FormField } from '..';
import { buildFieldName } from '../../utils/fieldNameUtils';

type Address = {
  houseNumber?: string;
  street?: string;
  zipCode?: string;
  city?: string;
  countryCode?: string;
};

export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}
/**
 * Component for address search.
 *
 * @remarks
 * Assumes the following fields exist in the form:
 * - county
 * - addressLine1
 * - eirCode
 * - town
 *
 * @todo Refactor to use safer field configuration.
 */

type AddressSearchFormFieldProps = {
  isDisabled?: boolean;
  fieldNamePrefix?: string;
};

export function AddressSearchFormField({ isDisabled, fieldNamePrefix }: AddressSearchFormFieldProps) {
  const { setFieldValue } = useFormikContext<Address>();

  const [query, setQuery] = useState('');

  const [validatedQuery, setValidatedQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchAddressResponse[]>([]);
  const debouncedQuery = useDebounce(query);

  const {
    data: results,
    isFetching: isLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isError: isSearchAddressError,
  } = useGetAddressQuery({ query: debouncedQuery }, { skip: debouncedQuery.length < 3 });

  const {
    data: validatedAddress,
    isFetching: isValidationLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isError: isValidateAddressError,
  } = useValidateAddressQuery({ query: validatedQuery }, { skip: validatedQuery === '' });

  useEffect(() => {
    setSearchResults(results || []);
  }, [results]);

  useEffect(() => {
    const { zipCode, city, street, houseNumber, county } = validatedAddress || {};

    setFieldValue(buildFieldName('eirCode', fieldNamePrefix), zipCode);
    setFieldValue(
      buildFieldName('addressLine1', fieldNamePrefix),
      houseNumber ? `${houseNumber} ${street || ''}` : `${street || ''}`
    );
    setFieldValue(buildFieldName('town', fieldNamePrefix), city);
    setFieldValue(buildFieldName('county', fieldNamePrefix), county?.replace(/^county\s+/i, '').trim());
    setValidatedQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatedAddress, setFieldValue]);

  return (
    <FormField
      name="addressSearch"
      labelText={t('addressSearchBar:label')}
      type="input"
      query={query}
      eager
      render={() => (
        <InputAutosuggest
          className="u-text-left"
          placeholder={t('addressSearchBar:placeholder')}
          list={searchResults?.map((item) => item.label)}
          value={query}
          awesompleteProps={{
            filter: () => true,
          }}
          disabled={isDisabled}
          addonIcon={isLoading || isValidationLoading || isDisabled ? undefined : 'semantic-search'}
          addonSpinner={isLoading || isValidationLoading ? <Spinner /> : null}
          openOnFocus
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onSelect={(e: any) => {
            setSearchResults([]);
            setQuery('');
            setValidatedQuery(e.text.value);
          }}
        />
      )}
    />
  );
}
